import { ChevronDown, ChevronRight, Folder, FolderOpen } from "lucide-react"
import { useState } from "react"

import type { Nodo } from "./tipos"

/**
 * El árbol de carpetas del Drive.
 *
 * ⚠ **Lo que no se puede abrir NO aparece**, ni con candado. El recorte lo hace el
 * servidor; aquí no hay ningún filtro que pudiera discrepar de él. En un almacén
 * interno el propio nombre de una carpeta —«Despidos», «Auditoría 2026»— ya es la
 * información que el permiso viene a proteger, así que enseñarla apagada la filtra
 * igual.
 */

function Rama({
  nodo,
  nivel,
  elegida,
  onElegir,
}: {
  nodo: Nodo
  nivel: number
  elegida: number | null
  onElegir: (id: number) => void
}) {
  const [abierta, setAbierta] = useState(nivel === 0)
  const activa = elegida === nodo.id
  const tieneHijas = nodo.hijas.length > 0

  return (
    <li>
      <div className="flex items-center">
        {/* El desplegar es un control aparte del elegir: pulsando el nombre se
            entra en la carpeta, no se colapsa lo que se estaba mirando. */}
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-label={abierta ? `Plegar ${nodo.nombre}` : `Desplegar ${nodo.nombre}`}
          aria-expanded={tieneHijas ? abierta : undefined}
          disabled={!tieneHijas}
          className="shrink-0 rounded-[var(--radius-control)] p-1 disabled:opacity-0"
          style={{ marginLeft: `${nivel * 14}px`, color: "var(--fg-muted)" }}
        >
          {abierta ? (
            <ChevronDown size={14} aria-hidden="true" />
          ) : (
            <ChevronRight size={14} aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={() => onElegir(nodo.id)}
          aria-current={activa ? "true" : undefined}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-control)] px-2 py-1.5 text-left text-sm toque-cristal"
          style={
            activa ? { backgroundColor: "var(--surface-elevated)", fontWeight: 500 } : undefined
          }
        >
          {activa ? (
            <FolderOpen size={15} aria-hidden="true" style={{ color: "var(--primary-texto)" }} />
          ) : (
            <Folder size={15} aria-hidden="true" style={{ color: "var(--fg-muted)" }} />
          )}
          <span className="truncate">{nodo.nombre}</span>
        </button>
      </div>
      {abierta && tieneHijas && (
        <ul>
          {nodo.hijas.map((hija) => (
            <Rama
              key={hija.id}
              nodo={hija}
              nivel={nivel + 1}
              elegida={elegida}
              onElegir={onElegir}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

export function ArbolCarpetas({
  raices,
  elegida,
  onElegir,
}: {
  raices: Nodo[]
  elegida: number | null
  onElegir: (id: number) => void
}) {
  if (raices.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        No tienes ninguna carpeta asignada todavía
      </p>
    )
  }
  return (
    <ul aria-label="Carpetas del Drive">
      {raices.map((nodo) => (
        <Rama key={nodo.id} nodo={nodo} nivel={0} elegida={elegida} onElegir={onElegir} />
      ))}
    </ul>
  )
}
